import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  text-align: center;
  color: #1f2937;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  background: #667eea;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
`;

const AddCategorySection = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 2px dashed #d1d5db;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  margin-bottom: 1rem;
  fontSize: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  margin-bottom: 1rem;
  fontSize: 1rem;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Button = styled.button`
  background: #667eea;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  fontWeight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #5a67d8;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
    transform: none;
  }
`;

const DeleteButton = styled(Button)`
  background: #dc2626;
  
  &:hover:not(:disabled) {
    background: #b91c1c;
  }
`;

const CategoriesList = styled.div`
  display: grid;
  gap: 1rem;
`;

const CategoryCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const CategoryName = styled.h3`
  color: #1f2937;
  margin: 0 0 0.5rem 0;
  fontSize: 1.2rem;
`;

const CategoryDescription = styled.p`
  color: #6b7280;
  margin: 0 0 1rem 0;
  fontSize: 0.9rem;
`;

const CategoryInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  fontSize: 0.8rem;
  color: #9ca3af;
  margin-bottom: 1rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

interface Category {
  id: number;
  room_id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
}

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: ''
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const roomId = searchParams.get('room_id');

  const loadCategories = useCallback(async () => {
    if (!roomId) return;
    try {
      setLoading(true);
      const response = await fetch(`https://api.eventoshub.suils.es/api/categories?room_id=${roomId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      if (result.success) {
        setCategories(result.data);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    if (roomId) {
      loadCategories();
    }
  }, [roomId, loadCategories]);

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategory.name.trim() || !roomId) return;

    try {
      const response = await fetch('https://api.eventoshub.suils.es/api/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          room_id: parseInt(roomId),
          name: newCategory.name.trim(),
          description: newCategory.description.trim() || null
        })
      });

      const result = await response.json();
      if (result.success) {
        setNewCategory({ name: '', description: '' });
        loadCategories();
      } else {
        alert('Error al crear categoría: ' + (result.message || result.error));
      }
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Error al crear categoría. Inténtalo de nuevo.');
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta categoría? Se perderán todos los votos asociados.')) return;

    try {
      const response = await fetch(`https://api.eventoshub.suils.es/api/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();
      if (result.success) {
        loadCategories();
      } else {
        alert('Error al eliminar categoría: ' + (result.message || result.error));
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error al eliminar categoría.');
    }
  };

  if (!roomId) {
    return (
      <Container>
        <Card>
          <Title>Error: Sala no especificada</Title>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>
            Debes acceder desde una sala específica.
          </p>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Card>
          <Title>⏳ Cargando categorías...</Title>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <BackButton onClick={() => navigate(`/dashboard?room_id=${roomId}`)}>
          ← Volver al Dashboard
        </BackButton>
        
        <Title>🏷️ Gestionar Categorías</Title>
        
        <AddCategorySection>
          <h3 style={{ color: '#374151', marginBottom: '1rem' }}>➕ Agregar Nueva Categoría</h3>
          <form onSubmit={handleCreateCategory}>
            <Input
              type="text"
              placeholder="Nombre de la categoría (ej: Mejor Amigo, Más Inteligente)"
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              required
            />
            <TextArea
              placeholder="Descripción (opcional)"
              value={newCategory.description}
              onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
            />
            <Button type="submit" disabled={!newCategory.name.trim()}>
              Crear Categoría
            </Button>
          </form>
        </AddCategorySection>

        {categories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 0', color: '#6b7280' }}>
            <p>No hay categorías en esta sala aún.</p>
            <p>¡Crea la primera categoría para comenzar a votar!</p>
          </div>
        ) : (
          <div>
            <h3 style={{ color: '#374151', marginBottom: '1.5rem' }}>
              📋 Categorías Actuales ({categories.length})
            </h3>
            <CategoriesList>
              {categories.map((category) => (
                <CategoryCard key={category.id}>
                  <CategoryName>{category.name}</CategoryName>
                  {category.description && (
                    <CategoryDescription>{category.description}</CategoryDescription>
                  )}
                  <CategoryInfo>
                    <span>Estado: {category.is_active ? '✅ Activa' : '❌ Inactiva'}</span>
                    <span>Creada: {new Date(category.created_at).toLocaleDateString()}</span>
                  </CategoryInfo>
                  <ActionButtons>
                    <DeleteButton 
                      onClick={() => handleDeleteCategory(category.id)}
                    >
                      🗑️ Eliminar
                    </DeleteButton>
                  </ActionButtons>
                </CategoryCard>
              ))}
            </CategoriesList>
          </div>
        )}
      </Card>
    </Container>
  );
};

export default CategoryManagement;